<template>
  <div class="header">
    <div class="header-content">
      <!-- Linke Seite: Logo und Firmenbezeichnung -->
      <div class="header-left">
        <div class="header-logo">
          <router-link to="/">
            <img src="../assets/images/logo.svg" alt="OdA Sozialberufe" class="logo" />
          </router-link>
        </div>
        <div class="header-title">
          <h1>OdA Tools</h1>
        </div>
      </div>
      
      <!-- Rechte Seite: ThemeSwitcher und Benutzermenü -->
      <div class="header-actions">
        <ThemeSwitcher />
        
        <!-- Online-Benutzer Dropdown (nur für Admins) -->
        <OnlineUsersDropdown v-if="isAuthenticated && isAdmin" />
        
        <!-- Benutzerprofilmenü mit Dropdown -->
        <div v-if="isAuthenticated" class="user-profile-menu">
          <button 
            class="user-profile-button" 
            @click="toggleUserMenu"
            :aria-expanded="isUserMenuOpen"
            aria-haspopup="true"
            data-testid="user-profile-button"
          >
            <div class="user-avatar">
              <span>{{ userInitials }}</span>
            </div>
            <div class="user-info">
              <span class="user-name">{{ user?.display_name || user?.email }}</span>
            </div>
            <svg
              class="chevron-icon"
              :class="{ 'chevron-icon--open': isUserMenuOpen }"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>

          <!-- Dropdown-Menü -->
          <transition name="dropdown">
            <div v-if="isUserMenuOpen" class="user-dropdown">
              <div class="user-dropdown-header">
                <div class="user-info">
                  <div class="user-avatar large">
                    <span>{{ userInitials }}</span>
                  </div>
                  <div class="user-details">
                    <div class="user-name">{{ user?.display_name || user?.email }}</div>
                    <div class="user-email">{{ user?.email }}</div>
                  </div>
                </div>
              </div>
              
              <div class="user-dropdown-content">
                <div class="dropdown-item disabled" title="Profil-Verwaltung wird in einer zukünftigen Version verfügbar sein">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <span>Profil</span>
                </div>
                
                <div class="dropdown-item disabled" title="Einstellungen werden in einer zukünftigen Version verfügbar sein">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="3"></circle>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                  </svg>
                  <span>Einstellungen</span>
                </div>
                
                <div class="dropdown-divider"></div>
                
                <button class="dropdown-item logout-item" @click="logout">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  <span>Abmelden</span>
                </button>
              </div>
            </div>
          </transition>
        </div>
        
        <!-- Login-Button für nicht angemeldete Benutzer -->
        <button v-else @click="handleLogin" class="login-button">Anmelden</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useAuthStore } from '../stores/auth';
import ThemeSwitcher from '../components/ThemeSwitcher.vue';
import OnlineUsersDropdown from '../components/admin/OnlineUsersDropdown.vue';

const authStore = useAuthStore();
const isUserMenuOpen = ref(false);

// Admin-Check
const isAdmin = computed(() => authStore.isAdmin);

// Computed properties
const isAuthenticated = computed(() => {
  const auth = authStore.isAuthenticated;
  console.log('[Header] isAuthenticated changed:', auth);
  return auth;
});
const user = computed(() => {
  const userData = authStore.user;
  console.log('[Header] user data changed:', userData ? { name: userData.display_name || userData.email, id: userData.id } : null);
  return userData;
});

// Benutzerinitialen für Avatar berechnen
const userInitials = computed(() => {
  if (!user.value?.display_name && !user.value?.email) return '?';
  
  const name = user.value?.display_name || user.value?.email || '';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
});

// Menu functions
const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
};

const closeUserMenu = () => {
  isUserMenuOpen.value = false;
};

const logout = async () => {
  try {
    await authStore.logout();
    closeUserMenu();
  } catch (error) {
    console.error('Logout-Fehler:', error);
  }
};

// Microsoft Login Handler
const handleLogin = async () => {
  try {
    await authStore.loginWithMicrosoft();
  } catch (error) {
    console.error('Login-Fehler:', error);
  }
};

// Click outside handler
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Element;
  // Nur schließen wenn nicht in user-profile-menu UND nicht in theme-switcher geklickt wurde
  if (!target.closest('.user-profile-menu') && !target.closest('.theme-switcher')) {
    closeUserMenu();
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
/* Header-Grundstruktur */
.header {
  display: flex;
  justify-content: center;
  background-color: var(--color-primary);
  color: white;
  box-shadow: var(--box-shadow-md);
  position: relative;
  z-index: 100;
  transition: all 0.3s ease;
  width: 100%;
  height: calc(67px + 2 * var(--spacing-md)); /* Logo-Höhe + Padding oben und unten */
  box-sizing: border-box;
}

/* Dark-Mode Anpassungen für Header */
:global(.mode-dark) .header {
  background-color: var(--color-background-dark);
  color: var(--color-text-light);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
}

/* Header-Content mit max-width */
.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  width: 100%;
  max-width: 1200px;
  height: 100%;
}

/* Linke Seite des Headers */
.header-left {
  display: flex;
  align-items: center;
}

.header-logo {
  margin-right: var(--spacing-md);
}

.logo {
  height: 67px;
  width: auto;
}

.header-title {
  flex: 1;
}

.header-title h1 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: inherit;
}

/* Rechte Seite des Headers */
.header-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

/* Login-Button */
.login-button {
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: var(--color-secondary);
  color: white;
  text-decoration: none;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  transition: background-color 0.2s;
}

.login-button:hover {
  background-color: var(--color-secondary-dark);
}

/* Benutzerprofilmenü */
.user-profile-menu {
  position: relative;
}

.user-profile-button {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-md);
  transition: background-color 0.2s;
}

.user-profile-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.user-profile-button:hover .user-avatar {
  background-color: var(--color-primary);
  color: var(--color-background);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--color-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: var(--spacing-sm);
  font-weight: 500;
  font-size: 0.85rem;
  overflow: hidden;
  position: relative;
}

.user-avatar.large {
  width: 40px;
  height: 40px;
  font-size: 1rem;
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-right: var(--spacing-sm);
}

.user-name {
  font-weight: 500;
  font-size: var(--font-size-sm);
  line-height: 1.2;
}

.user-details {
  font-size: var(--font-size-xs);
  opacity: 0.8;
  line-height: 1.2;
}

/* Dropdown */
.user-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 280px;
  background-color: var(--color-background);
  border-radius: var(--border-radius-md);
  box-shadow: var(--box-shadow-lg);
  overflow: hidden;
  z-index: 200;
}

.user-dropdown-header {
  padding: var(--spacing-md);
  background-color: var(--color-background-light);
  border-bottom: 1px solid var(--color-border);
}

.user-dropdown-header .user-info {
  display: flex;
  align-items: center;
}

.user-dropdown-header .user-details {
  margin-left: var(--spacing-md);
  overflow: hidden;
}

.user-dropdown-header .user-name {
  color: var(--color-text);
  font-weight: 600;
  margin-bottom: 4px;
  max-width: 180px;
}

.user-email {
  color: var(--color-text-light);
  font-size: var(--font-size-sm);
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dropdown-content {
  padding: var(--spacing-sm) 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: var(--spacing-sm) var(--spacing-md);
  color: var(--color-text);
  text-decoration: none;
  background: none;
  border: none;
  width: 100%;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: var(--font-size-sm);
}

.dropdown-item:hover {
  background-color: var(--color-background-light);
}

.dropdown-item svg {
  margin-right: var(--spacing-sm);
  flex-shrink: 0;
}

.dropdown-divider {
  height: 1px;
  background-color: var(--color-border);
  margin: var(--spacing-sm) 0;
}

.logout-item {
  color: var(--color-danger);
}

.logout-item:hover {
  background-color: var(--color-danger-light);
}

/* Disabled dropdown items */
.dropdown-item.disabled {
  opacity: 0.7;
  cursor: not-allowed;
  color: var(--color-text-dark);
}

.dropdown-item.disabled:hover {
  background-color: transparent;
  color: var(--color-text-dark);
}

.dropdown-item.disabled svg {
  opacity: 0.7;
}

/* Dark mode specific styling for disabled items */
[data-theme="dark"] .dropdown-item.disabled {
  color: var(--color-text-muted);
}

[data-theme="dark"] .dropdown-item.disabled:hover {
  color: var(--color-text-muted);
}

/* Dropdown-Animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Chevron-Icon */
.chevron-icon {
  transition: transform 0.2s;
}

.chevron-icon--open {
  transform: rotate(180deg);
}

/* Responsive Design */
@media (max-width: 768px) {
  .header {
    padding: var(--spacing-sm);
  }
  
  .header-title h1 {
    font-size: var(--font-size-md);
  }
  
  .user-profile-button .user-name {
    display: none;
  }
  
  .user-avatar {
    margin-right: 0;
  }
  
  .chevron-icon {
    display: none;
  }
}
</style>
